import { Address } from 'viem'

import { DEFAULT_CHALLENGE_OFFSET, DEFAULT_CHALLENGE_WINDOW } from '@src/configs/defaults'
import { ExchangeType } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteData } from '@src/types/order'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('eip712')

// EIP-712 type definitions for order placement
export const ORDER_BOOK_TYPE = {
    OrderDirection: [
        { name: 'srcAsset', type: 'address' },
        { name: 'dstAsset', type: 'bytes32' },
        { name: 'dstLzc', type: 'uint32' },
    ],
    OrderFunding: [
        { name: 'srcQuantity', type: 'uint96' },
        { name: 'dstQuantity', type: 'uint96' },
        { name: 'bondFee', type: 'uint16' },
        { name: 'bondAsset', type: 'address' },
        { name: 'bondAmount', type: 'uint96' },
    ],
    OrderExpiration: [
        { name: 'timestamp', type: 'uint32' },
        { name: 'challengeOffset', type: 'uint16' },
        { name: 'challengeWindow', type: 'uint16' },
    ],
    Order: [
        { name: 'direction', type: 'OrderDirection' },
        { name: 'funding', type: 'OrderFunding' },
        { name: 'expiration', type: 'OrderExpiration' },
        { name: 'target', type: 'bytes32' },
        { name: 'filler', type: 'address' },
    ],
}

// EIP-712 type definitions for CCTP transactions
export const CCTP_TYPE = {
    Deposit: [
        { name: 'amount', type: 'uint256' },
        { name: 'destinationDomain', type: 'uint32' },
        { name: 'mintRecipient', type: 'bytes32' },
        { name: 'burnToken', type: 'address' },
    ],
}

// Get domain data for EIP-712 typed data
export const getDomainData = (chainId: number, verifyingContract: Address) => {
    const domain = {
        name: 'TristeroOrderBook',
        version: '2.1',
        chainId,
        verifyingContract,
    }

    return domain
}

// Prepare EIP-712 typed data for order book transactions
export const prepareOrderBookTypedData = (
    sellData: IExchangeData,
    buyData: IExchangeData,
    quote: IQuoteData,
    swapContractAddress: Address,
    chainId: number
) => {
    const { order_direction, order_funding, order_expiration, filler_address, target_address } = quote.order_data

    try {
        // Create the domain data for EIP-712 signature
        const domain = getDomainData(chainId, swapContractAddress)

        // Create the typed data object
        // Ensure the message structure matches the types defined in ORDER_BOOK_TYPE
        const message = {
            // These need to be structured objects matching their respective type definitions
            direction: {
                srcAsset: order_direction.src_token_address,
                dstAsset: order_direction.dst_token_address,
                dstLzc: order_direction.dst_lzc,
            },
            funding: {
                srcQuantity: order_funding.src_amount_in,
                dstQuantity: order_funding.dst_amount_out,
                bondFee: order_funding.bond_fee,
                bondAsset: order_funding.bond_token_address,
                bondAmount: order_funding.bond_amount,
            },
            expiration: {
                timestamp: order_expiration?.timestamp ?? 0,
                challengeOffset: order_expiration?.challenge_offset ?? DEFAULT_CHALLENGE_OFFSET,
                challengeWindow: order_expiration?.challenge_window ?? DEFAULT_CHALLENGE_WINDOW,
            },
            target: target_address,
            filler: filler_address || '0x0000000000000000000000000000000000000000',
        }

        // Log the message structure to help debug
        logger.info('Preparing OrderBook typed data with message:', JSON.stringify(message, null, 2))

        // Return formatted EIP-712 typed data
        return {
            domain,
            types: ORDER_BOOK_TYPE,
            primaryType: 'Order' as const,
            message,
        }
    } catch (error) {
        logger.error('Error preparing typed data', error)
        throw error
    }
}

// Prepare EIP-712 typed data for CCTP transactions
export const prepareCctpTypedData = (
    amount: bigint,
    destinationDomain: number,
    mintRecipient: string, // bytes32 representation of the recipient address
    burnToken: Address,
    swapContractAddress: Address,
    chainId: number
) => {
    try {
        // Create the domain data for EIP-712 signature
        const domain = getDomainData(chainId, swapContractAddress)

        // Create the typed data object for CCTP deposit
        const message = {
            amount: amount.toString(),
            destinationDomain,
            mintRecipient,
            burnToken,
        }

        // Return formatted EIP-712 typed data
        return {
            domain,
            types: CCTP_TYPE,
            primaryType: 'Deposit' as const,
            message,
        }
    } catch (error) {
        logger.error('Error preparing CCTP typed data', error)
        throw error
    }
}

// Helper function to determine if a transaction type supports EIP-712
export const supportsEip712 = (exchangeType: string): boolean => {
    // Supporting both order book and CCTP transactions
    return exchangeType === ExchangeType.order_book || exchangeType === ExchangeType.cctp
}
